import { Injectable } from "@angular/core";
import { AuthenticatedApiService } from "rest-client";
import { BehaviorSubject, from, Observable } from "rxjs";
import { map, tap } from "rxjs/operators";
import { SourceAndQueryParams } from "../../states/trip-state/trip.model";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Store } from "@ngxs/store";
import { TripState } from "../../states/trip-state/trip.state";

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class VoucherService {
  private vouchersSubject = new BehaviorSubject<any[]>([]);
  clientVouchers$ = this.vouchersSubject.asObservable();
  private selectedVoucherCodeSubject = new BehaviorSubject<string | null>(null);
  selectedVoucherCode$ = this.selectedVoucherCodeSubject.asObservable();
  private availableVouchersSubject = new BehaviorSubject<any[]>([]);
  availableVouchers$ = this.availableVouchersSubject.asObservable();

  private sourceMode$: Observable<SourceAndQueryParams>;

  constructor(
    private api: AuthenticatedApiService,
    private store: Store
  ) {
    this.subscribeToSourceData();
  }

  app_mode = false;
  client_app_mode = false;

  fetchClientVouchers(): Observable<any[]> {
    return from(this.api.get('/client/promotion_vouchers').toPromise()).pipe(
      map((res: any) => res.promotion_vouchers || []),
      tap((vouchers) => this.vouchersSubject.next(vouchers))
    );
  }

  getPromotionVoucherDescription(id: number): Observable<any> {
    return from(this.api.get(`/client/promotion_voucher_description/${id}/html`));
  }



  listAvailableVouchers(data: { start_date: any; end_date: any, id_lot: any, id_product: any }): any[] {
    const startDate = new Date(data.start_date);
    const endDate = new Date(data.end_date);
    const stayDurationHours = (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60);

    const availableVouchers = this.vouchersSubject.getValue().filter((voucher) => {
      const bookingStart = new Date(voucher.booking_begin_date);
      const bookingEnd = new Date(voucher.booking_end_date);
      const minStayHours = parseInt(voucher.min_stay_hours, 10)
      const isInDateRange = bookingStart <= startDate && endDate <= bookingEnd;

      const onlyApp = parseInt(voucher.only_app, 10);
      const onlyWeb = parseInt(voucher.only_web, 10);

      const validStayLength = !minStayHours || isNaN(minStayHours) || stayDurationHours >= minStayHours;

      let matchesAppMode = true;

      if (this.app_mode || this.client_app_mode) {
        matchesAppMode = onlyApp !== 1 && onlyWeb !== 1;
      } else {
        matchesAppMode = onlyApp !== 1;
      }

      const matchesLot =
        data.id_lot > 0
          ? voucher.id_lot === '0' || voucher.id_lot === data.id_lot.toString()
          : true;

      const matchesProduct =
        data.id_product > 0
          ? voucher.id_product === '0' || voucher.id_product === data.id_product.toString()
          : true;

      return isInDateRange && matchesAppMode && validStayLength && matchesLot && matchesProduct;
    });

    this.setAvailableVouchers(availableVouchers);
    return availableVouchers;
  }

  setSelectedVoucherCode(code: string | null): void {
    this.selectedVoucherCodeSubject.next(code);
  }

  getSelectedVoucherCode(): string | null {
    return this.selectedVoucherCodeSubject.getValue();
  }

  setAvailableVouchers(vouchers: any[]): void {
    this.availableVouchersSubject.next(vouchers);
  }

  getAvailableVouchers(): any[] {
    return this.availableVouchersSubject.getValue();
  }

  private subscribeToSourceData(): void {
    this.sourceMode$ = this.store.select(TripState.sourceAndQueryParams);

    this.sourceMode$
      .pipe(untilDestroyed(this))
      .subscribe((sourceAndUp: SourceAndQueryParams) => {
        this.app_mode = !!sourceAndUp.app_mode;
        this.client_app_mode = !!sourceAndUp.client_app_mode;
      });
  }
}
